import { Box, HStack, Text, VStack, Spinner } from '@chakra-ui/react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface DashboardStatBoxProps {
  label: ReactNode;
  value: ReactNode;
  labelColor?: string;
  valueFontSize?: string;
  dateRange?: string;
  percentChange?: number;
  showLabelOutside?: boolean;
  isLoading?: boolean;
  href?: string;
  invertPercentChangeColors?: boolean;
}

export function DashboardStatBox({
  label,
  value,
  labelColor = 'gray.600',
  valueFontSize = 'sm',
  dateRange,
  percentChange,
  showLabelOutside = false,
  isLoading = false,
  href,
  invertPercentChangeColors = false,
}: DashboardStatBoxProps) {
  const hasPercentChange = percentChange != null && percentChange !== 0;

  // For metrics where lower is better (e.g., response time), invert the color logic
  const isPositiveChange = invertPercentChangeColors
    ? (percentChange ?? 0) < 0
    : (percentChange ?? 0) > 0;
  const changeColor = isPositiveChange ? 'green.600' : 'red.600';
  const ArrowIcon = (percentChange ?? 0) > 0 ? ArrowUp : ArrowDown;

  if (showLabelOutside) {
    const content = (
      <VStack flex={1} align="stretch" spacing={2}>
        <HStack justify="space-between">
          <Text fontSize="sm" fontWeight="medium" color={labelColor}>
            {label}
          </Text>
          {dateRange != null && dateRange !== '' ? (
            <Text fontSize="xs" color="gray.500">
              {dateRange}
            </Text>
          ) : null}
        </HStack>
        <Box
          bg="bg.surface"
          px={2}
          py={1}
          borderRadius="md"
          cursor={href != null && href !== '' ? 'pointer' : 'default'}
        >
          <HStack justify="space-between">
            {hasPercentChange ? (
              <HStack fontSize="sm" color={changeColor}>
                <ArrowIcon size={16} />
                <Text>{Math.abs(percentChange)}%</Text>
              </HStack>
            ) : (
              <Text>&nbsp;</Text>
            )}
            {isLoading ? (
              <Spinner size="xs" />
            ) : (
              <Text fontSize={valueFontSize} fontWeight="semibold">
                {value}
              </Text>
            )}
          </HStack>
        </Box>
      </VStack>
    );

    if (href != null && href !== '') {
      return (
        <Link to={href} style={{ textDecoration: 'none', flex: 1 }}>
          {content}
        </Link>
      );
    }

    return content;
  }

  const content = (
    <Box
      flex={1}
      bg="bg.surface"
      px={2}
      py={1}
      borderRadius="md"
      cursor={href != null && href !== '' ? 'pointer' : 'default'}
    >
      {(dateRange != null && dateRange !== '') || hasPercentChange ? (
        <VStack align="stretch" spacing={2}>
          <HStack justify="space-between" align="start">
            {dateRange != null && dateRange !== '' ? (
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" color={labelColor}>
                  {label}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {dateRange}
                </Text>
              </VStack>
            ) : (
              <Text fontSize="sm" color={labelColor}>
                {label}
              </Text>
            )}
            {isLoading ? (
              <Spinner size="xs" />
            ) : (
              <Text fontSize={valueFontSize} fontWeight="semibold">
                {value}
              </Text>
            )}
          </HStack>
          {hasPercentChange ? (
            <HStack fontSize="sm" color={changeColor}>
              <ArrowIcon size={16} />
              <Text>{Math.abs(percentChange)}%</Text>
            </HStack>
          ) : null}
        </VStack>
      ) : (
        <HStack justify="space-between">
          <Text fontSize="sm" color={labelColor}>
            {label}
          </Text>
          {isLoading ? (
            <Spinner size="xs" />
          ) : (
            <Text fontSize={valueFontSize} fontWeight="semibold">
              {value}
            </Text>
          )}
        </HStack>
      )}
    </Box>
  );

  if (href != null && href !== '') {
    return (
      <Link to={href} style={{ textDecoration: 'none', flex: 1 }}>
        {content}
      </Link>
    );
  }

  return content;
}
